import React, { useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import axiosInstance from "../utils/axiosSetup";
import { getQuarterOptions, getYearsRange } from "../utils/fileUploadGrid";
import { toast } from "react-toastify";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ClearIcon from "@mui/icons-material/Clear";
import { Box, CircularProgress, Typography, Autocomplete, TextField, Tooltip } from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';



type MatrixRow = {
    pm_name: string;
    Quarterly_Letter: boolean;
    Quarterly_Notes: boolean;
};


const PMDocumentMatrix: React.FC = () => {
    const [selectedQuarter, setSelectedQuarter] = useState<string>("");
    const [rowData, setRowData] = useState<MatrixRow[]>([]);
    const [loading, setLoading] = useState(false);
    const yearQuarters = getQuarterOptions();

    useEffect(() => {
        if (selectedQuarter) fetchMatrixData(selectedQuarter);
    }, [selectedQuarter]);


    const fetchMatrixData = async (yearQuarter: string, showToast = false) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(
                `/uploader/pm_quarterly_details?year_quarter=${encodeURIComponent(yearQuarter)}`
            );
            console.log("Response from report viewer", response)

            if (Array.isArray(response.data)) {
                if (response.data.length === 0) {
                    setRowData([])
                    toast.info("No records found.");
                } else {
                    setRowData(response.data);
                    if(showToast){
                        toast.success("Data refreshed successfully.")

                    }
                }

            } else {
                setRowData([]);
                toast.error("Invalid response format");
            }
        } catch (error: any) {
            console.error("Failed to fetch matrix data", error);
            setRowData([]);
            toast.error("Failed to load data. Something went wrong");
        } finally {
            setLoading(false);
        }
    };


    const columnDefs = useMemo<ColDef[]>(() => [
        {
            field: "pm_name",
            headerName: "PM Name",
            flex: 1.5,
            filter: "agTextColumnFilter",
            floatingFilter: true,
            sortable: true,
            sort: "asc",
        },
        {
            field: "Quarterly_Letter",
            headerName: "Quarterly Letter",
            cellRenderer: (params: any) =>
                params.value ?
                    <CheckBoxIcon color="success" />
                    :
                    <ClearIcon color="error" style={{ stroke: 'red', strokeWidth: 2 }} />,

            flex: 1,
            filter: "agSetColumnFilter",
            floatingFilter: true,
            sortable: true,
        },

        {
            field: "Quarterly_Notes",
            headerName: "Quarterly Notes",
            cellRenderer: (params: any) =>
                params.value ?
                    <CheckBoxIcon color="success" />
                    :
                    <ClearIcon color="error" style={{ stroke: 'red', strokeWidth: 2 }} />,
            flex: 1,
            filter: "agSetColumnFilter",
            floatingFilter: true,
            sortable: true,
        },


    ], []);


    return (

        <>
            <div className="flex" style={{
                alignItems: "center",

            }}>
                <div data-testid="report-quarter-selector" className="w-[160px] my-4">
                    <Autocomplete
                        options={yearQuarters}
                        value={selectedQuarter}
                        onChange={(e, newValue: any) => {
                            setSelectedQuarter(newValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Quarter"
                                variant="outlined"
                                inputProps={{
                                    ...params.inputProps,
                                    'data-testid': 'report-quarter-input',
                                }}
                            />
                        )}
                        disableClearable
                    />
                </div>
                <div className="ml-4">
                    <Tooltip title="Refresh data" arrow >
                    <button
                        data-testid="report-refresh-button"
                    
                        onClick={() => fetchMatrixData(selectedQuarter, true)}
                        disabled={!selectedQuarter || loading}
                        style={{
                            padding: "7px",
                            border: "none",
                            borderRadius: 4,
                            backgroundColor: !selectedQuarter || loading ? "#ccc" : "#1976d2",
                            color: "white",
                            cursor: !selectedQuarter || loading ? "not-allowed" : "pointer",
                            fontWeight: 500,
                            
                        }}
                    >
                        <RefreshIcon />
                    </button>
                    </Tooltip>
                </div>
            </div>

            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" mt={5}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading data....
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ flexGrow: 1, minHeight: 0 }}   >
                    <div data-testid="report-matrix" className="ag-theme-alpine" style={{ height: "100%", width: "100%" }} >
                        <AgGridReact
                            rowData={rowData}
                            columnDefs={columnDefs}
                            domLayout="normal"
                            suppressMovableColumns
                            suppressColumnVirtualisation

                        />
                    </div>
                </Box>

            )}
        </>

    )


}

export default PMDocumentMatrix;